import { isSystemTag } from './system-tags';
import {
  stripHiddenLinkedEntitiesBlock,
  withLinkedEntityMentions,
  type EntityMentionOption,
} from './entity-mentions';

export function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids)];
}

export function intersectIds(lists: string[][]): string[] {
  if (lists.length === 0) {
    return [];
  }
  const [first, ...rest] = lists;
  const restSets = rest.map((list) => new Set(list));
  return uniqueIds(first).filter((id) => restSets.every((set) => set.has(id)));
}

export function diffIds(previous: string[], next: string[]): { added: string[]; removed: string[] } {
  const previousSet = new Set(previous);
  const nextSet = new Set(next);
  return {
    added: uniqueIds(next).filter((id) => !previousSet.has(id)),
    removed: uniqueIds(previous).filter((id) => !nextSet.has(id)),
  };
}

export function applyTagAssignment(currentTags: string[], added: string[], removed: string[]): string[] {
  const removedSet = new Set(removed.map((tag) => tag.trim().toLowerCase()).filter(Boolean));
  const next: string[] = [];
  const seen = new Set<string>();

  for (const tag of currentTags) {
    const normalized = tag.trim().toLowerCase();
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    if (!isSystemTag(normalized) && removedSet.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    next.push(normalized);
  }

  for (const tag of added) {
    const normalized = tag.trim().toLowerCase();
    if (!normalized || isSystemTag(normalized) || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    next.push(normalized);
  }

  return next;
}

export function applyLinkedEntityAssignment(params: {
  notes: string | null;
  currentCharacterIds: string[];
  currentLocationIds: string[];
  addedCharacterIds: string[];
  removedCharacterIds: string[];
  addedLocationIds: string[];
  removedLocationIds: string[];
  characterOptionsById: Map<string, EntityMentionOption>;
  locationOptionsById: Map<string, EntityMentionOption>;
}): string {
  const removedCharacters = new Set(params.removedCharacterIds);
  const removedLocations = new Set(params.removedLocationIds);
  const nextCharacterIds = uniqueIds([
    ...params.currentCharacterIds.filter((id) => !removedCharacters.has(id)),
    ...params.addedCharacterIds,
  ]);
  const nextLocationIds = uniqueIds([
    ...params.currentLocationIds.filter((id) => !removedLocations.has(id)),
    ...params.addedLocationIds,
  ]);
  const visibleNotes = stripHiddenLinkedEntitiesBlock(params.notes ?? '');
  return withLinkedEntityMentions(
    visibleNotes,
    nextCharacterIds,
    nextLocationIds,
    params.characterOptionsById,
    params.locationOptionsById
  );
}
